import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Mobile-safe confirm dialog — replaces window.confirm() which is blocked
 * by many mobile browsers (especially inside PWAs / touch contexts).
 *
 * Usage:
 *   <ConfirmButton
 *     title="Supprimer l'étude ?"
 *     description="Cette action est irréversible."
 *     onConfirm={() => handleDelete(id)}
 *     trigger={<Button size="icon"><Trash2 /></Button>}
 *   />
 */
export const ConfirmButton = ({
  trigger,
  title = 'Confirmer ?',
  description = '',
  confirmLabel = 'Supprimer',
  cancelLabel = 'Annuler',
  destructive = true,
  onConfirm,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setOpen(true);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    try {
      await Promise.resolve(onConfirm?.());
    } catch {
      /* handled upstream */
    }
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: handleTriggerClick, 'data-testid': trigger.props['data-testid'] })}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md w-[92vw]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
            {description && (
              <AlertDialogDescription className="text-zinc-400">{description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-end gap-2">
            <AlertDialogCancel
              className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white m-0"
              data-testid="confirm-cancel"
            >
              {cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                destructive
                  ? 'bg-red-600 hover:bg-red-500 text-white m-0'
                  : 'bg-emerald-600 hover:bg-emerald-500 text-white m-0'
              }
              data-testid="confirm-action"
            >
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
